import { TreeNode } from "@/types/tree";
import { findNodeParent } from "@/lib/tree/tree";
import { documentStore } from "@/store/store-accessor";
import {
  ADVANCED_SLIDE_SLUG,
  ADVANCED_SLIDER_SLUG,
  GRID_SLUG,
  WRAPPER_LINK_SLUG
} from "@/lib/elements/constants";
import { forceNextTick } from "@/util/force-next-tick";

export function findNearestRepeatableItem(
  startNode: TreeNode
): TreeNode | null {
  let current = startNode;

  if (isImmediateChildOfContainerThatSupportsRepeatingChildren(current)) {
    return current;
  }

  while (current) {
    const parent = findNodeParent(documentStore.document.tree, current);
    if (!parent) break;

    if (isImmediateChildOfContainerThatSupportsRepeatingChildren(parent)) {
      return parent;
    }

    current = parent;
  }

  return null;
}

export function findNearestWrapperLink(startNode: TreeNode): TreeNode | null {
  let current = startNode;

  if (current.data.type === WRAPPER_LINK_SLUG) {
    return current;
  }

  while (current) {
    const parent = findNodeParent(documentStore.document.tree, current);
    if (!parent) break;

    if (parent.data.type === WRAPPER_LINK_SLUG) {
      return parent;
    }

    current = parent;
  }

  return null;
}

export function isChildOrDescendantOfRepeatableParent(
  element: TreeNode
): boolean {
  let current = element;
  while (current) {
    const parent = findNodeParent(documentStore.document.tree, current);
    if (!parent) break;

    if (isContainerThatSupportsRepeatingChildren(parent)) {
      return true;
    }

    current = parent;
  }

  return false;
}

export function isImmediateChildOfContainerThatSupportsRepeatingChildren(
  element: TreeNode
) {
  const parent = findNodeParent(documentStore.document.tree, element);
  if (!parent) return false;
  if (isContainerThatSupportsRepeatingChildren(parent)) return true;
  return false;
}

export function isWrapperLink(element: TreeNode) {
  if (element.data.type === WRAPPER_LINK_SLUG) return true;
  return false;
}

export function isChildOrDescendantOfWrapperLink(element: TreeNode): boolean {
  let current = element;
  while (current) {
    const parent = findNodeParent(documentStore.document.tree, current);
    if (!parent) break;

    if (parent.data.type === WRAPPER_LINK_SLUG) {
      return true;
    }

    current = parent;
  }

  return false;
}

export function shouldShowRepeatableItemOrWrapperLinkOutline(
  element: TreeNode
): boolean {
  return (
    isChildOrDescendantOfRepeatableParent(element) ||
    isChildOrDescendantOfWrapperLink(element)
  );
}

export function isContainerThatSupportsRepeatingChildren(
  parent: TreeNode
): boolean {
  return (
    parent.data.type === GRID_SLUG || parent.data.type === ADVANCED_SLIDER_SLUG
  );
}

export function handleAddAnother(element: TreeNode) {
  const pointer : { nodeId?: TreeNode["id"] } = {};
  documentStore.duplicateElement({ elementId: element.id, pointer: pointer });

  if (element.data.type === ADVANCED_SLIDE_SLUG) {
    forceNextTick(() => {
      const newSlideId = pointer.nodeId;
      const doc = window.Breakdance.canvas.document;
      const win = doc.defaultView;
      // @ts-ignore
      win.BreakdanceSwiper().selectSlide(newSlideId);
    });
  }
}

export function handleMovePrevious(element: TreeNode) {
  const parent = findNodeParent(documentStore.document.tree, element);
  if (!parent) return;

  const currentIndex = parent.children.findIndex(child => child.id === element.id);
  if (currentIndex <= 0) return;

  documentStore.moveElement({
    draggedElementId: element.id,
    droppedOnElementId: parent.id,
    index: currentIndex - 1
  });
}

export function handleMoveNext(element: TreeNode) {
  const parent = findNodeParent(documentStore.document.tree, element);
  if (!parent) return;

  const currentIndex = parent.children.findIndex(child => child.id === element.id);
  if (currentIndex === -1 || currentIndex >= parent.children.length - 1) return;

  documentStore.moveElement({
    draggedElementId: element.id,
    droppedOnElementId: parent.id,
    index: currentIndex + 1
  });
}
